import React, { useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';

const API = process.env.REACT_APP_API_URL || '/api';

export default function PhoneVerification({ onVerified, onClose, phoneField = 'phone' }) {
  const { t } = useLanguage();
  const [step, setStep] = useState(1); // 1=enter phone, 2=enter code
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const sendCode = async () => {
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/phone/verify/start`, { phone, phone_field: phoneField });
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || t.phoneVerify?.sendError || 'Failed to send code');
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async () => {
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/phone/verify/confirm`, { phone, code, phone_field: phoneField });
      setSuccess(true);
      setTimeout(() => {
        if (onVerified) onVerified(phone);
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || t.phoneVerify?.verifyError || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center',
      justifyContent: 'center', zIndex: 2000, padding: '2rem'
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#111827', border: '1px solid rgba(148,163,184,0.3)',
        padding: '2.5rem', maxWidth: '420px', width: '100%'
      }}>
        <h2 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '1.8rem', color: '#E8ECF0', marginBottom: '1.5rem' }}>
          {t.phoneVerify?.title || 'Verify Phone Number'}
        </h2>

        {error && (
          <div style={{ padding: '0.8rem', marginBottom: '1rem', background: 'rgba(220,70,70,0.15)', border: '1px solid rgba(220,70,70,0.4)', color: '#d07070', fontSize: '0.85rem' }}>
            {error}
          </div>
        )}

        {success ? (
          <div style={{ padding: '1.5rem', textAlign: 'center', background: 'rgba(100,200,100,0.15)', border: '1px solid rgba(100,200,100,0.4)', color: '#70d070' }}>
            {t.phoneVerify?.success || 'Phone verified successfully!'}
          </div>
        ) : step === 1 ? (
          <div>
            <p style={{ color: '#94A3B8', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
              {t.phoneVerify?.enterPhone || 'Enter your phone number to receive a verification code via SMS.'}
            </p>
            <div className="mb-3">
              <label className="form-label">{t.phoneVerify?.phoneLabel || 'Phone Number'}</label>
              <input
                type="tel"
                className="form-control"
                placeholder="+1 514-000-0000"
                value={phone}
                onChange={e => setPhone(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button className="btn btn-outline-primary" onClick={onClose}>
                {t.common?.cancel || 'Cancel'}
              </button>
              <button className="btn btn-primary" onClick={sendCode} disabled={!phone || loading}>
                {loading ? (t.phoneVerify?.sending || 'Sending...') : (t.phoneVerify?.sendCode || 'Send Code')}
              </button>
            </div>
          </div>
        ) : (
          <div>
            <p style={{ color: '#94A3B8', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
              {t.phoneVerify?.enterCode || 'Enter the 6-digit code sent to'} <strong style={{ color: '#E8ECF0' }}>{phone}</strong>
            </p>
            <div className="mb-3">
              <label className="form-label">{t.phoneVerify?.codeLabel || 'Verification Code'}</label>
              <input
                type="text"
                className="form-control"
                maxLength={6}
                placeholder="123456"
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                style={{ letterSpacing: '0.3em', textAlign: 'center', fontSize: '1.2rem' }}
                autoFocus
              />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button className="btn btn-outline-primary" onClick={() => { setStep(1); setCode(''); setError(''); }}>
                {t.common?.back || 'Back'}
              </button>
              <button className="btn btn-primary" onClick={verifyCode} disabled={code.length !== 6 || loading}>
                {loading ? (t.phoneVerify?.verifying || 'Verifying...') : (t.phoneVerify?.verify || 'Verify')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
